import { useEffect, useState } from "react";
import "./AdminLogin.css";
import Logo from "../../src/Logo/A1LogoNoBackgroud.png";
import {
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaLock,
  FaArrowRight,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth } from "../Firebase";
import { useNavigate } from "react-router-dom";
import logger from "../Components/Logger";

// Utility functions to set, get, and delete cookies for email and persist login preferences
// with a default expiration of 30 days and SameSite=Lax for security.
const setCookie = (name, value, days = 30) => {
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

// Utility functions to get and delete cookies for email and persist login preferences
const getCookie = (name) => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : "";
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

export function AdminLogin({ setConnected }) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(true);
  const [persistLogin, setPersistLogin] = useState(false);
  const [hasConsent] = useState(true); // no banner, always allow cookie use

  // Load saved email and persistLogin preference from cookies on component mount
  useEffect(() => {
    const savedEmail = getCookie("admin_email");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
      logger.info("Loaded saved email from cookie.");
    } else {
      setRememberEmail(false);
      logger.info("No saved email cookie found.");
    }

    const savedPersist = getCookie("admin_persist_login");
    if (savedPersist) {
      setPersistLogin(savedPersist === "true");
      logger.info("Loaded saved persistLogin from cookie.");
    } else {
      logger.info("No saved persistLogin cookie found.");
      setPersistLogin(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      logger.warn("Invalid email format entered.");
      return setError("Format d'email invalide.");
    }
    setLoading(true);
    try {
      // persistance de session (local vs session) : n’affecte pas la config Firebase
      const persistenceMode = persistLogin
        ? browserLocalPersistence
        : browserSessionPersistence;
      await setPersistence(auth, persistenceMode);

      await signInWithEmailAndPassword(auth, cleanEmail, password);

      // Handle cookie storage based on user preferences and consent
      // Store email in cookie if user has consented and wants to be remembered, otherwise delete any existing cookie for 30 days
      if (hasConsent && rememberEmail) {
        setCookie("admin_email", cleanEmail, 30);
        logger.info("Email saved in cookie for future sessions.");
      } else {
        // Delete the email cookie if the user does not want to be remembered or has not consented
        deleteCookie("admin_email");
        logger.info("Email cookie deleted.");
      }

      // Store persist login preference in cookie if user has consented, otherwise delete any existing cookie for 30 days
      if (hasConsent && persistLogin) {
        setCookie("admin_persist_login", "true", 30);
        logger.info("Persist login preference saved in cookie.");
      } else {
        deleteCookie("admin_persist_login");
        logger.info("Persist login cookie deleted.");
      }

      setConnected(true);
      navigate("/admin");
    } catch (err) {
      logger.error("Invalid credentials entered.");
      setError("Invalid credentials. Please try again.");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="logo-area" translate="no">
          <img src={Logo} alt="Logo" className="login-logo" />
        </div>
        <h2><span>Admin Login</span></h2>
        <p className="subtitle"><span>Secure access to your management dashboard</span></p>

        <div className="input-group">
          <label htmlFor="email">
            <FaEnvelope /> Email
          </label>
          <input
            type="email"
            placeholder="exemple@gmail.com"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="input-group">
          <label htmlFor="password">
            <FaLock /> Password
          </label>
          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <button
              //https://stackoverflow.com/questions/77676529/how-to-correctly-toggle-type-of-password-input-to-text-or-vice-versa-in-reactne
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex="-1"
            >
              {showPassword ? (
                <FaEye key="eye-open" />
              ) : (
                <FaEyeSlash key="eye-closed" />
              )}
            </button>
          </div>
        </div>

        <div className="checkbox-row">
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={rememberEmail}
              onChange={(e) => {
                const checked = e.target.checked;
                setRememberEmail(checked);

                if (!checked) {
                  deleteCookie("admin_email");
                }
              }}
              disabled={loading}
            />
            <span>Remember my email</span>
          </label>
          <label className="checkbox-item">
            <input
              type="checkbox"
              checked={persistLogin}
              onChange={(e) => {
                const checked = e.target.checked;
                setPersistLogin(checked);

                if (!checked) {
                  deleteCookie("admin_persist_login");
                }
              }}
              disabled={loading}
            />
            <span>Stay connected</span>
          </label>
        </div>

        {error && (
          <div key="error-display" className="error-message">
            <FaExclamationTriangle className="error-icon" />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" className="login-button" disabled={loading}>
          <span>{loading ? "Signing In..." : "Sign In"}</span>

          {loading ? (
            <FaSpinner key="loader" className="btn-spinner" />
          ) : (
            <FaArrowRight key="arrow" className="btn-arrow" />
          )}
        </button>
      </form>
    </div>
  );
}
